import { useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, ChevronRight, Loader2, CheckCircle2, Clock, AlertCircle, FileText, RefreshCw } from "lucide-react";
import { useGetOpenExamDietsQuery } from "../../api/examsApi";
import { useExaminerGetAssignmentsQuery } from "../../api/examinerApi";

const STATUS_MAP: Record<string, { cls: string; label: string }> = {
  draft:            { cls: "bg-gray-50 text-gray-400 border-gray-100",     label: "Draft" },
  submitted:        { cls: "bg-blue-50 text-blue-700 border-blue-100",     label: "Submitted" },
  under_review:     { cls: "bg-amber-50 text-amber-700 border-amber-100",  label: "Under Review" },
  rework:           { cls: "bg-rose-50 text-rose-700 border-rose-100",     label: "Rework" },
  approved:         { cls: "bg-emerald-50 text-emerald-700 border-emerald-100", label: "Approved" },
  under_moderation: { cls: "bg-purple-50 text-purple-700 border-purple-100", label: "Moderation" },
  published:        { cls: "bg-emerald-50 text-emerald-700 border-emerald-100", label: "Published" },
};

export default function ExaminerDashboardPage() {
  const { data: diets = [], isLoading: dietsLoading, refetch, isFetching } = useGetOpenExamDietsQuery();
  const [selectedDiet, setSelectedDiet] = useState<string>("");

  const { data: assignments = [], isLoading: assignsLoading } = useExaminerGetAssignmentsQuery(
    selectedDiet || undefined,
    { skip: !selectedDiet }
  );

  const totalCourses  = assignments.length;
  const pending       = assignments.filter((a: any) => ["draft","rework"].includes(a.status ?? "draft")).length;
  const submitted     = assignments.filter((a: any) => a.status === "submitted").length;
  const done          = assignments.filter((a: any) => ["approved","published"].includes(a.status ?? "")).length;

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4 md:p-6 pb-20">

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Courses Assigned", val: totalCourses, icon: BookOpen,      color: "indigo" },
          { label: "Pending Entry",    val: pending,      icon: Clock,          color: "amber"  },
          { label: "Submitted",        val: submitted,    icon: FileText,       color: "blue"   },
          { label: "Completed",        val: done,         icon: CheckCircle2,   color: "emerald"},
        ].map((s, i) => (
          <div key={i} className="bg-white border border-gray-100 p-5 rounded-3xl shadow-sm flex items-center gap-4">
            <div className={`w-11 h-11 rounded-2xl flex items-center justify-center bg-${s.color}-50 text-${s.color}-600 shrink-0`}>
              <s.icon className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">{s.label}</div>
              <div className="text-xl font-black text-gray-900">{selectedDiet ? s.val : "—"}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="w-14 h-14 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl shadow-gray-200">
              <BookOpen className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-gray-900 tracking-tight leading-none uppercase">My Assigned Courses</h1>
              <p className="text-gray-400 text-sm mt-1.5 font-medium">Select an exam diet to view your courses and enter scores.</p>
            </div>
          </div>
          <button onClick={() => refetch()}
            className="p-3 rounded-xl border bg-white hover:bg-gray-50 transition-all active:scale-95 shadow-sm">
            <RefreshCw className={`w-4 h-4 text-indigo-500 ${isFetching ? "animate-spin" : ""}`} />
          </button>
        </div>

        {/* Diet selector */}
        <div className="mt-6 pt-6 border-t border-gray-50">
          <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Select Exam Diet</label>
          {dietsLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-400 mt-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading diets…
            </div>
          ) : (
            <select value={selectedDiet} onChange={(e) => setSelectedDiet(e.target.value)}
              className="mt-2 w-full sm:w-72 border-b-2 border-gray-100 py-2 font-bold text-gray-800 outline-none focus:border-indigo-500 bg-transparent text-sm">
              <option value="">— Choose a diet —</option>
              {diets.map((d: any) => (
                <option key={d.id} value={d.id}>{d.name ?? d.title ?? d.id}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Course list */}
      {selectedDiet && (
        <div className="bg-white border border-gray-100 rounded-[2.5rem] shadow-sm overflow-hidden">
          <div className="px-8 py-5 border-b border-gray-50 flex items-center justify-between">
            <div className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Course Assignments</div>
            {assignsLoading && <Loader2 className="w-4 h-4 animate-spin text-gray-300" />}
          </div>

          {assignsLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-gray-200" />
            </div>
          ) : assignments.length === 0 ? (
            <div className="py-24 text-center">
              <AlertCircle className="w-10 h-10 mx-auto text-gray-100 mb-3" />
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">No courses assigned for this diet</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {assignments.map((a: any) => {
                const status = (a.status ?? "draft").toLowerCase();
                const badge  = STATUS_MAP[status] ?? STATUS_MAP.draft;
                return (
                  <Link key={a.id}
                    to={`/exams/examiner/courses/${a.course_id}/diet/${a.diet_id ?? selectedDiet}`}
                    className="flex items-center justify-between px-8 py-5 hover:bg-gray-50/80 transition-all group">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-2xl bg-indigo-50 flex items-center justify-center">
                        <BookOpen className="w-4 h-4 text-indigo-500" />
                      </div>
                      <div>
                        <p className="font-black text-gray-900 uppercase tracking-tight group-hover:text-indigo-600 transition-colors">
                          {a.course_name ?? `Course ${a.course_id}`}
                        </p>
                        {a.course_code && (
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">{a.course_code}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border shadow-sm ${badge.cls}`}>
                        {badge.label}
                      </span>
                      <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-indigo-400 transition-colors" />
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      )}

      {!selectedDiet && !dietsLoading && (
        <div className="bg-gray-900 text-white rounded-[2.5rem] p-10 flex items-center gap-8 shadow-2xl shadow-gray-200">
          <div className="w-16 h-16 bg-indigo-500/10 rounded-3xl flex items-center justify-center shrink-0 border border-indigo-500/20">
            <BookOpen className="w-8 h-8 text-indigo-400" />
          </div>
          <div>
            <h4 className="text-[11px] font-black uppercase tracking-[0.4em] text-indigo-400 mb-2">Select a Diet</h4>
            <p className="text-sm text-gray-400 leading-relaxed font-medium">
              Choose an exam diet above to load your assigned courses and begin score entry.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
